// docs/62 — emotes on the board. The heroes standing on the board are registered as on screen
// (so their emotes sound here), and a hero who emotes gets the bubble over their head: drawn in the
// screen-fixed layer at a whole-pixel 2× (crisp at any board zoom) and following the unit as it
// walks, from the same atlas and frame timing as the DOM bubbles. A ranger's companion is on screen
// too (its owner's wheel picks it) and its pet emotes wiggle it, leaning toward its ranger.
import Phaser from 'phaser';
import { EMOTE_ATLAS, EMOTE_PX, EMOTES, PET_EMOTE_MS, PET_EMOTES, atlasFrame, frameAt } from '../../core/emote/catalogue';
import { putOnScreen, useEmotes } from '../../core/emote/store';
import { getConn, identityHex } from '../../core/net/connection';
import type { Snapshot } from '../../core/sim/types';
import type { BoardMapper } from './BoardCamera';
import type { UnitSprite } from './UnitSprite';

export const EMOTE_TEXTURE = 'emotes';
const SCALE = 2;
/** A ranger's companion carries the ranger's id with bit 40 set (docs/59): not a hero of its own. */
const COMPANION = 2 ** 40;

/** Queue the atlas on a loader (Boot). */
export function loadEmotes(load: Phaser.Loader.LoaderPlugin): void {
  load.spritesheet(EMOTE_TEXTURE, EMOTE_ATLAS, { frameWidth: EMOTE_PX, frameHeight: EMOTE_PX });
}

/** The hero's owner (its player, not whoever controls it now: only the owner may emote with it). */
function ownerOf(heroId: bigint): string | undefined {
  const h = getConn()?.db.hero.id.find(heroId);
  return h ? identityHex(h.owner) : undefined;
}

export class EmoteLayer {
  private drawn = new Map<string, { seq: number; img: Phaser.GameObjects.Image }>();
  /** The pet emote last wiggled, by companion id. */
  private wiggled = new Map<string, number>();
  private release?: () => void;
  private heroesKey = '';

  constructor(private scene: Phaser.Scene, private layer: Phaser.GameObjects.Container, private mapper: BoardMapper, private cell: number) {}

  /** The heroes and companions on this board are on screen (re-registered when the line-up changes). */
  setBoard(snapshot: Snapshot | undefined, me: string): void {
    const heroes = (snapshot?.units ?? [])
      .filter((u) => u.heroId !== undefined && u.onBoard !== false)
      // a companion is its ranger's owner's to emote with
      .map((u) => ({ id: BigInt(u.heroId!), name: u.name, mine: !!me && ownerOf(BigInt(u.heroId! >= COMPANION ? u.heroId! - COMPANION : u.heroId!)) === me }));
    const key = heroes.map((h) => `${h.id}:${h.mine ? 1 : 0}`).join('|');
    if (key === this.heroesKey) return;
    this.heroesKey = key;
    this.release?.();
    this.release = putOnScreen(heroes);
  }

  /** Each frame: start, advance, move and end the bubbles. */
  update(units: Map<number, UnitSprite>, still: boolean): void {
    const active = useEmotes.getState().active;
    for (const [id, d] of this.drawn) {
      if (active[id]?.seq !== d.seq) { d.img.destroy(); this.drawn.delete(id); }
    }
    const byHero = new Map<string, UnitSprite>();
    for (const u of units.values()) if (u.unit.heroId !== undefined) byHero.set(String(u.unit.heroId), u);
    this.wiggle(byHero, still);
    if (!this.scene.textures.exists(EMOTE_TEXTURE)) return;
    for (const [id, a] of Object.entries(active)) {
      const u = Number(id) < COMPANION ? byHero.get(id) : undefined;
      const f = still ? EMOTES[a.emote]?.frame : frameAt(a.at);
      let d = this.drawn.get(id);
      if (!u || f === undefined) { if (d) { d.img.setVisible(false); } continue; }
      if (!d) {
        const img = this.scene.add.image(0, 0, EMOTE_TEXTURE, atlasFrame(a.emote, f)).setOrigin(0.5, 1).setScale(SCALE);
        this.layer.add(img);
        d = { seq: a.seq, img };
        this.drawn.set(id, d);
      }
      d.img.setFrame(atlasFrame(a.emote, f)).setVisible(u.visible);
      // the unit's pivot is the bottom centre of its square; the bubble sits on the top of the figure
      const p = this.mapper.toScreen(u.x, u.y - this.cell + 2);
      d.img.setPosition(p.x, p.y);
    }
  }

  /** Each new pet emote wiggles its companion once (not under reduced motion: its sound still plays). */
  private wiggle(byHero: Map<string, UnitSprite>, still: boolean): void {
    const pets = useEmotes.getState().pets;
    for (const [id, a] of Object.entries(pets)) {
      if (this.wiggled.get(id) === a.seq) continue;
      this.wiggled.set(id, a.seq);
      const u = byHero.get(id);
      const mood = PET_EMOTES[a.emote]?.key;
      if (!u || !mood || still) continue;
      const ranger = byHero.get(String(Number(id) - COMPANION));
      u.wiggle(mood, ranger ? Math.sign(ranger.x - u.x) || 1 : 1, PET_EMOTE_MS);
    }
  }

  /** Heroes with a bubble showing (e2e). */
  drawnIds(): string[] { return [...this.drawn].filter(([, d]) => d.img.visible).map(([id]) => id); }

  destroy(): void {
    this.release?.(); this.release = undefined; this.heroesKey = '';
    for (const d of this.drawn.values()) d.img.destroy();
    this.drawn.clear();
  }
}
